// Search controller — modular, no ?s= / no page reload.
//
// Intercepts the [mad_search_bar] form, fetches the CURRENT page with ?q=<query>
// (a harmless param that never triggers WordPress search routing / the Brizy
// swap), and swaps in the freshly rendered .mad-search-ai + .mad-search-list
// regions. Reuses all server-side rendering, then re-inits the AI loader + pager.
//
//  - data-url-sync="true"  -> reflect ?q= in the URL (shareable, back button)
//  - data-url-sync="false" -> pure widget, URL untouched
//
// No-JS fallback: the form just GET-submits ?q= (a normal page load) and the
// server renders results the same way.

var skeleton = require('./Skeleton');

// Rows shown while the request is in flight. Matches the default page size
// closely enough to keep the page height stable.
var SKELETON_ROWS = 4;

// Swap every results list for skeleton rows, returning a restore callback for
// the failure path (a dead request must not leave the user staring at ghosts).
function showSkeletons() {
  var lists = document.querySelectorAll('.mad-search-list');
  var saved = [];

  for (var i = 0; i < lists.length; i++) {
    saved.push({ el: lists[i], html: lists[i].innerHTML });
    lists[i].classList.add('is-loading');
    lists[i].innerHTML = skeleton.listHtml(SKELETON_ROWS);
  }

  return function restore() {
    for (var j = 0; j < saved.length; j++) {
      saved[j].el.innerHTML = saved[j].html;
      saved[j].el.classList.remove('is-loading');
    }
  };
}

// Show the AI-overview skeleton immediately, in lockstep with the results
// skeletons. Returns a restore callback for the failure path.
function showAiSkeletons(query) {
  var blocks = document.querySelectorAll('.mad-search-ai');
  var saved = [];

  for (var i = 0; i < blocks.length; i++) {
    var el = blocks[i];
    var body = el.querySelector('.mad-search-ai__body');
    saved.push({
      el: el,
      body: body,
      wasHidden: el.hasAttribute('hidden'),
      wasLoading: el.classList.contains('is-loading'),
      bodyHtml: body ? body.innerHTML : null
    });

    if (!query) {
      el.setAttribute('hidden', ''); // Nothing to summarize.
      continue;
    }

    el.removeAttribute('hidden');
    el.classList.add('is-loading');
    if (body) {
      body.innerHTML = skeleton.aiBodyHtml();
      body.setAttribute('aria-busy', 'true');
    }
  }

  return function restoreAi() {
    for (var j = 0; j < saved.length; j++) {
      var s = saved[j];
      if (s.wasHidden) { s.el.setAttribute('hidden', ''); } else { s.el.removeAttribute('hidden'); }
      if (!s.wasLoading) { s.el.classList.remove('is-loading'); }
      if (s.body && s.bodyHtml !== null) { s.body.innerHTML = s.bodyHtml; }
    }
  };
}

function canEnhance() {
  return typeof window.fetch === 'function' &&
         typeof window.DOMParser === 'function' &&
         typeof window.URLSearchParams === 'function';
}

function updateInputs(query) {
  var inputs = document.querySelectorAll('.mad-search-bar__input');
  for (var i = 0; i < inputs.length; i++) {
    inputs[i].value = query;
  }
}

function swapRegion(doc, selector) {
  var incoming = doc.querySelectorAll(selector);
  var existing = document.querySelectorAll(selector);
  var n = Math.min(incoming.length, existing.length);
  for (var i = 0; i < n; i++) {
    existing[i].outerHTML = incoming[i].outerHTML;
  }
}

// Adopt ?s= when the page arrived with it (search template); otherwise use ?q=.
function activeParam() {
  var params = new URLSearchParams(window.location.search);
  return params.has('s') && !params.has('q') ? 's' : 'q';
}

function buildUrl(query) {
  var key = activeParam();
  var params = new URLSearchParams(window.location.search);
  if (query) {
    params.set(key, query);
  } else {
    params.delete(key);
  }
  var qs = params.toString();
  return window.location.pathname + (qs ? '?' + qs : '');
}

function runSearch(query, pushUrl) {
  var url = buildUrl(query);
  var restoreLists = showSkeletons();
  var restoreAi = showAiSkeletons(query);
  function restore() { restoreLists(); restoreAi(); }

  fetch(url, { method: 'GET', credentials: 'same-origin' })
    .then(function (res) { return res.ok ? res.text() : null; })
    .then(function (html) {
      if (html === null) {
        restore();
        return;
      }
      var doc;
      try {
        doc = new DOMParser().parseFromString(html, 'text/html');
      } catch (e) {
        restore();
        return;
      }

      swapRegion(doc, '.mad-search-ai');
      swapRegion(doc, '.mad-search-list');
      updateInputs(query);

      if (window.madSearchInitAi) { window.madSearchInitAi(document); }
      if (window.madSearchInitPager) { window.madSearchInitPager(document); }

      if (pushUrl) {
        history.pushState({ q: query }, '', url);
      }
    })
    .catch(function () { restore(); /* previous results stay; user can retry */ });
}

function hasTargets() {
  return document.querySelector('.mad-search-list, .mad-search-ai') !== null;
}

function onSubmit(e) {
  var form = e.target;
  if (!form || !form.classList || !form.classList.contains('mad-search-bar__form')) {
    return;
  }
  if (!canEnhance()) {
    return; // Let the browser submit ?q= normally.
  }
  if (!hasTargets()) {
    // Nothing on this page to fill in place -> let the form navigate (to `target`
    // or reload the current page with ?q=), like a classic search box.
    return;
  }

  e.preventDefault();

  var input = form.querySelector('.mad-search-bar__input');
  var query = input ? input.value.trim() : '';
  var pushUrl = form.getAttribute('data-url-sync') !== 'false';

  runSearch(query, pushUrl);
}

function onPopState() {
  if (!canEnhance()) {
    return;
  }
  var params = new URLSearchParams(window.location.search);
  runSearch(params.get('q') || params.get('s') || '', false);
}

function initSearchApp() {
  // Delegated so it keeps working after region swaps.
  document.addEventListener('submit', onSubmit, false);
  window.addEventListener('popstate', onPopState, false);
}

if (document.readyState !== 'loading') {
  initSearchApp();
} else {
  document.addEventListener('DOMContentLoaded', initSearchApp);
}

module.exports = {};
